import { Injectable, inject } from '@angular/core'

import type { Observable } from 'rxjs'
import { BehaviorSubject, catchError, map, of } from 'rxjs'

import { AuthorsService } from '../api/authors-service/authors.service'
import type { AuthorDto } from '../api/responses'
import { FilterSelectionService } from '../services/filter-selection.service'
import type { FilterAuthor, FilterSearchConfig } from '../types/types'

@Injectable({
  providedIn: 'root',
})
export class AuthorSearchService {
  private readonly authorsService = inject(AuthorsService)
  private readonly filterSelection = inject(FilterSelectionService)

  private readonly state = new BehaviorSubject<FilterSearchConfig>({
    authorSearchTerm: '',
    isLoadingAuthors: false,
    currentPage: 1,
    itemsPerPage: -1,
    totalAuthors: 0,
    isInitialLoad: true,
  })

  readonly searchConfig$ = this.state.asObservable()

  search(term: string): Observable<FilterAuthor[]> {
    this.filterSelection.setCurrentAuthorPage(1)
    const currentState = this.state.value
    this.state.next({
      ...currentState,
      authorSearchTerm: term,
      isLoadingAuthors: true,
    })

    return this.authorsService
      .getAuthors(1, 150, 'last_name', 'asc', { name: { like: term } })
      .pipe(
        map(response => {
          if (!response?.Authors?.docs) {
            this.state.next({
              ...this.state.value,
              isLoadingAuthors: false,
              totalAuthors: 0,
              isInitialLoad: false,
            })
            return []
          }

          const authors = response.Authors.docs
          const searchTerm = term?.toLowerCase() ?? ''

          // Get current selections from service
          const currentAuthors = this.filterSelection.getCurrentAuthors()
          const selectedIds = currentAuthors
            .filter((a: FilterAuthor) => a.selected)
            .map((a: FilterAuthor) => a.id)

          // Filter authors based on search term if present
          const filteredAuthors = !searchTerm
            ? authors
            : authors.filter(author => {
                const fullName =
                  `${author.first_name ?? ''} ${author.last_name ?? ''}`.toLowerCase()
                return fullName.includes(searchTerm)
              })

          this.state.next({
            ...this.state.value,
            isLoadingAuthors: false,
            totalAuthors: filteredAuthors.length,
            isInitialLoad: false,
            currentPage: 1,
          })

          // Map authors to FilterAuthor type, preserving existing selections
          return filteredAuthors.map((author: AuthorDto) => ({
            id: author.id,
            name: `${author.first_name ?? ''} ${author.last_name ?? ''}`.trim(),
            selected: selectedIds.includes(author.id),
            additionalProps: {
              first_name: author.first_name,
              last_name: author.last_name,
              pre_title: author.pre_title,
              middle_name: author.middle_name,
              initials: author.initials,
              post_title: author.post_title,
            },
          }))
        }),
        catchError((error: unknown) => {
          console.error('Error searching authors:', error)
          this.state.next({
            ...this.state.value,
            isLoadingAuthors: false,
            totalAuthors: 0,
            isInitialLoad: false,
          })
          return of([])
        })
      )
  }

  setLoading(isLoading: boolean): void {
    this.state.next({
      ...this.state.value,
      isLoadingAuthors: isLoading,
    })
  }

  resetSearch(): void {
    this.state.next({
      ...this.state.value,
      authorSearchTerm: '',
      currentPage: 1,
      isInitialLoad: true,
    })
  }
}
